<template>
  <component
    :is="isLink ? 'router-link' : 'button'"
    class="BaseButton w-full relative py-3 px-4 flex items-center justify-center text-center rounded-2xl transition duration-200 hover:scale-[1.02] select-none outline-none"
    :class="{
      'h-[32px] text-sm !rounded-lg': size === SIZES.EXTRA_SMALL,
      'min-h-[40px] text-base': size === SIZES.SMALL,
      'min-h-[56px] text-xl': size === SIZES.MEDIUM,
      'min-h-[65px] text-xl': size === SIZES.BIG,
      'BaseButton__dark bg-dark border-shadow text-white focus:ring-4 focus:ring-gray-300 active:bg-[#525252]':
        colors.isDark,
      'BaseButton__light bg-white border-shadow text-gray-900 focus:ring-4 focus:ring-gray-100 active:bg-gray-200':
        colors.isLight,
      'BaseButton__transparent bg-transparent text-[#7C7C7C] focus:ring-4 focus:ring-gray-100 active:text-gray-900':
        colors.isTransparent,
      'BaseButton__primary-gradient border-shadow text-white bg-gradient-to-r from-[#FFA52F] to-[#CE3434] focus:ring-4 focus:ring-[#FFA52F] hover:bg-gradient-to-bl':
        colors.isPrimaryGradient,
      'cursor-not-allowed': isDisabled || isLoading,
      'opacity-25': isDisabled
      // 'active:scale-105': !isDisabled
    }"
    v-bind="isLink ? { to } : {}"
    @click="handleClick"
  >
    <BaseLoader
      v-if="isLoading"
      :color="loaderColor"
      class="absolute inset-0 scale-50"
    />

    <span
      class="font-bold leading-6"
      :class="{
        'opacity-0': isLoading
      }"
    >
      <slot />
    </span>
  </component>
</template>

<script>
import BaseLoader from '@/components/Base/BaseLoader.vue'

const COLORS = Object.freeze({
  DARK: 'dark',
  LIGHT: 'light',
  TRANSPARENT: 'transparent',
  PRIMARY_GRADIENT: 'primary-gradient'
})

const SIZES = Object.freeze({
  EXTRA_SMALL: 'extra-small',
  SMALL: 'small',
  MEDIUM: 'medium',
  BIG: 'big'
})

export default {
  name: 'BaseButton',

  components: { BaseLoader },

  props: {
    color: { type: String, default: COLORS.DARK },

    isLoading: { type: Boolean, default: false },
    isDisabled: { type: Boolean, default: false },

    size: { type: String, default: SIZES.MEDIUM },

    to: { type: Object, default: null }
  },

  computed: {
    isLink() {
      return this.to !== null
    },

    loaderColor() {
      if (this.colors.isDark) return 'white'
      if (this.colors.isLight) return 'dark'
      if (this.colors.isTransparent) return 'dark'
      if (this.colors.isPrimaryGradient) return 'white'

      return 'dark'
    },

    colors() {
      return {
        isDark: this.color === COLORS.DARK,
        isLight: this.color === COLORS.LIGHT,
        isTransparent: this.color === COLORS.TRANSPARENT,
        isPrimaryGradient: this.color === COLORS.PRIMARY_GRADIENT
      }
    }
  },

  created() {
    this.SIZES = SIZES
  },

  mounted() {
    // For IPhones
    this.$el.addEventListener('touchstart', this.addActiveTouch)
    this.$el.addEventListener('touchend', this.removeActiveTouch)
  },

  methods: {
    handleClick() {
      if (this.isLoading) return
      if (this.isDisabled) return

      setTimeout(() => {
        this.$emit('click')
      }, 100)
    },

    addActiveTouch() {
      this.$el.classList.add('active')
    },

    removeActiveTouch() {
      this.$el.classList.remove('active')
    },

    triggerCorrectAnimation() {
      this.$el.classList.add('correct')
    },

    triggerIncorrectAnimation() {
      this.$el.classList.add('incorrect')
    }
  }
}
</script>

<style lang="scss" scoped>
.BaseButton {
  animation: button-pop 0.25s ease-out;

  &__dark.active {
    @apply bg-[#525252];
  }

  &__light.active {
    @apply bg-dark text-white;
  }

  &__transparent.active {
    @apply text-gray-900;
  }

  &.correct {
    @apply bg-green-200;
  }

  &.incorrect {
    @apply bg-red-200;
  }
}

@keyframes button-pop {
  0% {
    transform: scale(0.98);
  }
  40% {
    transform: scale(1.02);
  }
  100% {
    transform: scale(1);
  }
}
</style>
